import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import type {
  SpiritualServiceDto,
  MemberServiceTypeDto,
  CreateMemberServicePayload,
  UpdateMemberServicePayload,
  ServicesPolicyResponseAll,
  ServicePolicyResponseSingle,
  ServicePolicy,
  ServicesPolicyLegend
} from "../../types/profiles/memberServicesTypes";

import {
  fetchServicesCatalogApi,
  fetchMyServicesApi,
  createMyServiceApi,
  updateMyServiceApi,
  deleteMyServiceApi,
  fetchServicesPolicyApi,
  fetchServicePolicyApi,
} from "../../services/profiles/servicesApi";

// ---------- Helpers ----------
const toErrorString = (err: any): string => {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (typeof data?.detail === "string") return data.detail;
  if (typeof data?.message === "string") return data.message;
  if (data && typeof data === "object") return JSON.stringify(data);
  return err?.message || "Unexpected error.";
};

// ---------- Thunks ----------
export const fetchServicesCatalog = createAsyncThunk<
  SpiritualServiceDto[],
  void,
  { rejectValue: string }
>("memberServices/fetchServicesCatalog", async (_, { rejectWithValue }) => {
  try {
    return await fetchServicesCatalogApi();
  } catch (err: any) {
    return rejectWithValue(toErrorString(err) || "Failed to load services catalog.");
  }
});

export const fetchMyServices = createAsyncThunk<
  MemberServiceTypeDto[],
  void,
  { rejectValue: string }
>("memberServices/fetchMyServices", async (_, { rejectWithValue }) => {
  try {
    return await fetchMyServicesApi();
  } catch (err: any) {
    return rejectWithValue(toErrorString(err) || "Failed to load your services.");
  }
});

export const createMyService = createAsyncThunk<
  MemberServiceTypeDto,
  CreateMemberServicePayload,
  { rejectValue: string }
>("memberServices/createMyService", async (payload, { rejectWithValue }) => {
  try {
    return await createMyServiceApi(payload);
  } catch (err: any) {
    return rejectWithValue(toErrorString(err) || "Failed to create service.");
  }
});

export const updateMyService = createAsyncThunk<
  MemberServiceTypeDto,
  UpdateMemberServicePayload,
  { rejectValue: string }
>("memberServices/updateMyService", async (payload, { rejectWithValue }) => {
  try {
    return await updateMyServiceApi(payload);
  } catch (err: any) {
    return rejectWithValue(toErrorString(err) || "Failed to update service.");
  }
});

export const deleteMyService = createAsyncThunk<
  number, // return deleted id
  number,
  { rejectValue: string }
>("memberServices/deleteMyService", async (id, { rejectWithValue }) => {
  try {
    await deleteMyServiceApi(id);
    return id;
  } catch (err: any) {
    return rejectWithValue(toErrorString(err) || "Failed to delete service.");
  }
});

// --- NEW: policies (all) ---
export const fetchServicesPolicy = createAsyncThunk<
  ServicesPolicyResponseAll,
  void,
  { rejectValue: string }
>("memberServices/fetchServicesPolicy", async (_, { rejectWithValue }) => {
  try {
    return await fetchServicesPolicyApi();
  } catch (err: any) {
    return rejectWithValue(toErrorString(err) || "Failed to load services policy.");
  }
});

// --- NEW: policy (single by service code) ---
export const fetchServicePolicy = createAsyncThunk<
  ServicePolicyResponseSingle,
  string, // service code (e.g., "children")
  { rejectValue: string }
>("memberServices/fetchServicePolicy", async (serviceCode, { rejectWithValue }) => {
  try {
    return await fetchServicePolicyApi(serviceCode);
  } catch (err: any) {
    return rejectWithValue(toErrorString(err) || "Failed to load service policy.");
  }
});

// ---------- State ----------
export type MemberServicesState = {
  catalog: SpiritualServiceDto[];
  myServices: MemberServiceTypeDto[];

  // NEW: policy state
  policy: Record<string, ServicePolicy>;    // map: service_code -> policy
  policyLabels: Record<string, string>;
  policyLegend: ServicesPolicyLegend | null;
  loadingPolicy: boolean;

  loadingCatalog: boolean;
  loadingList: boolean;
  creating: boolean;
  updating: boolean;
  deleting: boolean;

  error: string | null;
  successMessage: string | null;
};

const initialState: MemberServicesState = {
  catalog: [],
  myServices: [],

  policy: {},
  policyLabels: {},
  policyLegend: null,
  loadingPolicy: false,

  loadingCatalog: false,
  loadingList: false,
  creating: false,
  updating: false,
  deleting: false,

  error: null,
  successMessage: null,
};

const memberServicesSlice = createSlice({
  name: "memberServices",
  initialState,
  reducers: {
    clearMemberServicesMessage(state) {
      state.successMessage = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Catalog
    builder
      .addCase(fetchServicesCatalog.pending, (state) => {
        state.loadingCatalog = true;
        state.error = null;
      })
      .addCase(fetchServicesCatalog.fulfilled, (state, action: PayloadAction<SpiritualServiceDto[]>) => {
        state.loadingCatalog = false;
        state.catalog = action.payload || [];
      })
      .addCase(fetchServicesCatalog.rejected, (state, action) => {
        state.loadingCatalog = false;
        state.error = action.payload || "Failed to load services catalog.";
      });

    // My services list
    builder
      .addCase(fetchMyServices.pending, (state) => {
        state.loadingList = true;
        state.error = null;
        state.myServices = [];
      })
      .addCase(fetchMyServices.fulfilled, (state, action: PayloadAction<MemberServiceTypeDto[]>) => {
        state.loadingList = false;
        state.myServices = action.payload || [];
      })
      .addCase(fetchMyServices.rejected, (state, action) => {
        state.loadingList = false;
        state.myServices = [];
        state.error = action.payload || "Failed to load your services.";
      });

    // Create
    builder
      .addCase(createMyService.pending, (state) => {
        state.creating = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createMyService.fulfilled, (state, action: PayloadAction<MemberServiceTypeDto>) => {
        state.creating = false;
        if (action.payload) state.myServices.unshift(action.payload);
        state.successMessage = "Service added successfully.";
      })
      .addCase(createMyService.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload || "Failed to create service.";
      });

    // Update
    builder
      .addCase(updateMyService.pending, (state) => {
        state.updating = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateMyService.fulfilled, (state, action: PayloadAction<MemberServiceTypeDto>) => {
        state.updating = false;
        const updated = action.payload;
        const idx = state.myServices.findIndex((s) => s.id === updated.id);
        if (idx >= 0) state.myServices[idx] = updated;
        state.successMessage = "Service updated successfully.";
      })
      .addCase(updateMyService.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload || "Failed to update service.";
      });

    // Delete
    builder
      .addCase(deleteMyService.pending, (state) => {
        state.deleting = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteMyService.fulfilled, (state, action: PayloadAction<number>) => {
        state.deleting = false;
        const id = action.payload;
        state.myServices = state.myServices.filter((s) => s.id !== id);
        state.successMessage = "Service removed successfully.";
      })
      .addCase(deleteMyService.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload || "Failed to delete service.";
      });

    // --- NEW: policies (all) ---
    builder
      .addCase(fetchServicesPolicy.pending, (state) => {
        state.loadingPolicy = true;
        state.error = null;
      })
      .addCase(fetchServicesPolicy.fulfilled, (state, action: PayloadAction<ServicesPolicyResponseAll>) => {
        state.loadingPolicy = false;
        state.policy = action.payload.policy || {};
        state.policyLabels = action.payload.labels || {};
        state.policyLegend = action.payload.legend ?? null; // ← این خط مهم است
      })
      .addCase(fetchServicesPolicy.rejected, (state, action) => {
        state.loadingPolicy = false;
        state.error = action.payload || "Failed to load services policy.";
      });

    // --- NEW: policy (single) ---
    builder
      .addCase(fetchServicePolicy.pending, (state) => {
        state.loadingPolicy = true;
        state.error = null;
      })
      .addCase(fetchServicePolicy.fulfilled, (state, action) => {
        state.loadingPolicy = false;
        const resp = action.payload as ServicePolicyResponseSingle;
        const serviceCode = action.meta.arg;
        if (resp?.policy && serviceCode) {
          state.policy[serviceCode] = resp.policy;
        }
        if (resp?.labels) {
          state.policyLabels = { ...state.policyLabels, ...resp.labels };
        }
        if (resp?.legend) {
          state.policyLegend = resp.legend;
        }
      })
      .addCase(fetchServicePolicy.rejected, (state, action) => {
        state.loadingPolicy = false;
        state.error = action.payload || "Failed to load service policy.";
      });
  },
});

export const { clearMemberServicesMessage } = memberServicesSlice.actions;
export default memberServicesSlice.reducer;
